import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'app-create-scorm-language-info',
    imports: [FormsModule, MatIconModule, TranslateModule],
    templateUrl: './create-scorm-language-info.component.html'
})
export class CreateScormLanguageInfoComponent implements OnInit {
  @Input() language!: 'FR' | 'EN' | 'ES';
  @Input() nom = '';
  @Input() pdfFile: File | null = null;
  @Input() subtitleFile: File | null = null;
  @Input() description = '';
  @Input() contentType: 'pdf' | 'video' | 'multi-chapitres' = 'pdf';
  @Input() showContentUpload = true;
  @Input() isPrimary = false;
  @Input() vimeoId = '';
  @Input() vimeoIdError = '';
  @Input() videoFile: File | null = null;
  @Input() existingPdfFileName: string | null = null;
  @Input() isEditMode = false;
  @Input() isVideoUploading = false;
  @Input() videoUploadProgress = 0;
  @Output() nomChange = new EventEmitter<string>();
  @Output() pdfFileChange = new EventEmitter<File | null>();
  @Output() subtitleFileChange = new EventEmitter<File | null>();
  @Output() descriptionChange = new EventEmitter<string>();
  @Output() vimeoIdChange = new EventEmitter<string>();
  @Output() videoFileChange = new EventEmitter<File | null>();
  @Output() existingPdfRemoved = new EventEmitter<void>();

  isDragging = false;
  fileError = '';
  videoMode: 'file' | 'vimeo' = 'file';

  ngOnInit(): void {
    if (this.vimeoId) {
      this.videoMode = 'vimeo';
    }
  }

  private readonly labels: Record<'FR' | 'EN' | 'ES', string> = {
    FR: 'Français',
    EN: 'Anglais',
    ES: 'Espagnol'
  };
  private readonly inLanguage: Record<'FR' | 'EN' | 'ES', string> = {
    FR: 'en français',
    EN: 'en anglais',
    ES: 'en espagnol'
  };

  get languageLabel(): string {
    return this.labels[this.language] ?? '';
  }

  get titrePlaceholder(): string {
    return `Saisir le titre ${this.inLanguage[this.language] ?? ''}`;
  }

  get descriptionPlaceholder(): string {
    return `Saisir la description ${this.inLanguage[this.language] ?? ''}`;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    this.fileError = '';

    const files = event.dataTransfer?.files;
    if (!files?.length) return;

    const file = files[0];
    const fileExt = file.name.split('.').pop()?.toLowerCase();

    if (this.contentType === 'pdf') {
      if (file.type === 'application/pdf' || fileExt === 'pdf') {
        this.pdfFile = file;
        this.pdfFileChange.emit(this.pdfFile);
      } else {
        this.fileError = 'Please upload a PDF file';
        setTimeout(() => this.fileError = '', 3000);
      }
    } else if (this.contentType === 'video') {
      if (file.type === 'text/vtt' || fileExt === 'vtt' || fileExt === 'srt') {
        this.subtitleFile = file;
        this.subtitleFileChange.emit(this.subtitleFile);
      } else {
        this.fileError = 'Please upload a VTT or SRT file';
        setTimeout(() => this.fileError = '', 3000);
      }
    }
  }

  onFileSelected(event: Event, fileType: 'pdf' | 'subtitle' | 'video'): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      if (fileType === 'pdf') {
        this.pdfFile = input.files[0];
        this.pdfFileChange.emit(this.pdfFile);
      } else if (fileType === 'subtitle') {
        this.subtitleFile = input.files[0];
        this.subtitleFileChange.emit(this.subtitleFile);
      } else if (fileType === 'video') {
        this.vimeoId = '';
        this.videoFile = input.files[0];
        this.videoFileChange.emit(this.videoFile);
      }
    }
  }

  removeFile(fileType: 'pdf' | 'subtitle' | 'video'): void {
    if (fileType === 'pdf') {
      this.pdfFile = null;
      this.pdfFileChange.emit(null);
    } else if (fileType === 'subtitle') {
      this.subtitleFile = null;
      this.subtitleFileChange.emit(null);
    } else if (fileType === 'video') {
      this.videoFile = null;
      this.videoFileChange.emit(null);
    }
  }

  onVimeoIdInput(value: string): void {
    this.vimeoId = value;
    if (value) {
      this.videoFile = null;
    }
    this.vimeoIdChange.emit(value);
  }
}
